import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { KeyRound, GraduationCap, Copy, Loader2 } from "lucide-react";

export function StudentsManagement({ gameId, refreshKey, teams, onRefresh }) {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [target, setTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [issued, setIssued] = useState({});
  const [movingId, setMovingId] = useState(null);

  const fetchStudents = useCallback(async () => {
    if (!gameId) return;
    try {
      const res = await axios.get(`${API}/games/${gameId}/students`);
      setStudents(res.data || []);
    } catch {
      setStudents([]);
    }
  }, [gameId, refreshKey]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const moveStudent = async (student, newTeamId) => {
    if (newTeamId === student.team_id) return;
    setMovingId(student.id);
    try {
      const res = await axios.put(`${API}/games/${gameId}/students/${student.id}/team`, { team_id: newTeamId });
      toast.success(t("instructor.studentMoved", {
        name: res.data.student_name || student.name,
        from: res.data.from_team,
        to: res.data.to_team,
      }));
      await fetchStudents();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("instructor.studentMoveFailed"));
    } finally {
      setMovingId(null);
    }
  };

  const confirmReset = async () => {    setResetting(true);
    try {
      const res = await axios.put(`${API}/games/${gameId}/students/${target.id}/reset-password`);
      const pw = res.data.new_password;
      setIssued((prev) => ({ ...prev, [target.id]: pw }));
      toast.success(t("instructor.newPasswordToast", { email: res.data.email, password: pw }), { duration: 20000 });
      setTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("instructor.resetFailed"));
    } finally {
      setResetting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(t("instructor.copyPassword"));
  };

  return (
    <Card data-testid="students-management">
      <CardHeader className="border-b border-slate-100">
        <CardTitle className="font-heading flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-sky-600" />
          {t("instructor.students")}
        </CardTitle>
        <CardDescription>{t("instructor.studentsHintV2")}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {students.length === 0 ? (
          <p className="py-10 text-center text-slate-500" data-testid="students-empty">{t("instructor.noStudents")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]">
              <tbody>
                {students.map((s) => (
                  <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`student-row-${s.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{s.name || t("instructor.student")}</div>
                      <div className="text-xs text-slate-500">{s.email}</div>
                    </td>
                    <td className="py-3 px-4">
                      <Select
                        value={s.team_id}
                        onValueChange={(v) => moveStudent(s, v)}
                        disabled={movingId === s.id}
                      >
                        <SelectTrigger className="h-9 w-[190px]" data-testid={`student-team-select-${s.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(teams || []).map((tm) => (
                            <SelectItem key={tm.id} value={tm.id} data-testid={`student-team-option-${s.id}-${tm.id}`}>
                              {tm.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4">
                      {issued[s.id] && (
                        <button
                          onClick={() => copy(issued[s.id])}
                          className="flex items-center gap-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded font-mono text-sm text-amber-700"
                          data-testid={`issued-password-${s.id}`}
                        >
                          {issued[s.id]}
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-2"
                        onClick={() => setTarget(s)}
                        data-testid={`reset-student-pw-${s.id}`}
                      >
                        <KeyRound className="w-4 h-4" />
                        {t("instructor.resetPassword")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="reset-student-pw-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("instructor.resetPasswordTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("instructor.resetPasswordDesc", { email: target?.email || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-student-pw-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmReset(); }}
              disabled={resetting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="reset-student-pw-confirm"
            >
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("instructor.resetPassword")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
